import { useMutation, useQuery } from "@tanstack/react-query";
import { memo, useState } from "react";
import { Accordion } from "react-bootstrap";
import { withNamespaces } from "react-i18next";
import { RiInformationLine, RiLinkM } from "react-icons/ri";
import { toast } from "react-toastify";
import { Alert, Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import SupplierRiskAssessmentService from "src/modules/3rd-party-management/apis/admin/SupplierRiskAssessmentService";

const CATEGORY_CONFIG = {
    common_country: {
        title: "Country-Related Risks",
        description:
            "Risks identified for third parties operating in the same or similar countries."
    },
    common_nace_code: {
        title: "Industry-Related Risks",
        description:
            "Risks associated with third parties operating in the same industry classification (NACE)."
    },
    common_product: {
        title: "Product-Related Risks",
        description:
            "Risks linked to similar products or commodities supplied by other third parties."
    }
};

const decodeHTMLEntities = (text) => {
    const parser = new DOMParser();
    const decodedText = parser.parseFromString(
        `<!doctype html><body>${text}`,
        "text/html"
    ).body.textContent;
    return decodedText;
};

const ConfirmUseRiskModal = ({ 
    t,
    isOpen, 
    toggle, 
    selectedRisk, 
    onConfirm,
    isLoading
}) => {
    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {t('Confirm Risk Selection')}
            </ModalHeader>
            <ModalBody>
                <h5>
                    {t('Selected Risk')}
                </h5>
                <p>
                    <strong>{t('Title')}:</strong> {selectedRisk?.title}
                </p>
                <p>
                    <strong>{t('Description')}:</strong>
                    {selectedRisk?.description ? (
                        decodeHTMLEntities(selectedRisk?.description).replace(/<[^>]+>/g, "")
                    ) : null} 
                </p>
                <p>
                    <strong>{t('Created by')}:</strong> {selectedRisk?.createdBy}
                </p>

                <hr />
                <p>
                    {t('By using this risk, it will be associated with the current third party.')}
                    {t('You can only select one risk at a time. If you do not want to use this risk, you can cancel and choose another or create a new risk.')}
                </p>
            </ModalBody>
            <ModalFooter>
                <Button 
                    color="secondary" 
                    onClick={toggle}
                    disabled={isLoading}
                >
                    {t('Cancel')}
                </Button>
                <Button 
                    color="primary" 
                    onClick={() => onConfirm(selectedRisk)}
                    disabled={isLoading}
                >
                    {isLoading && (
                        <Spinner size='sm' className="me-1" />
                    )}
                    {t('Confirm')}
                </Button>
            </ModalFooter>
        </Modal>
    );
}

const ExistingRisksContent = ({
    supplierId,
    t,
    onLinkingComplete
}) => {
    const [ selectedRiskToUse, setSelectedRiskToUse ] = useState(null);

    const {
        data: suggestionList,
        isFetching: fetchingInprocess
    } = useQuery({
        queryKey: [
            "3rd-party-management-fetch-risk-suggestion-list",
            supplierId
        ],
        queryFn: async () => {
            const service = SupplierRiskAssessmentService.getInstance();

            return await service.fetchSuggestionList(supplierId, {
                supplier: supplierId
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("Unable to load suggested risks. Please try again later."), {
                type: "error"
            });
        }
    });

    const handleLinkRisk = useMutation({
        mutationFn: async ({ id }) => {
            const service = SupplierRiskAssessmentService.getInstance();
            return await service.linkRisk(id, {
                supplier: supplierId
            })
        },
        onError: () => {
            toast(t('Failed to link the risk. Please try again.'), {
                type: 'error'
            })
        },
        onSuccess: () => {
            toast(t('The risk has been successfully linked to this third party.'), {
                type: 'success'
            });

            onLinkingComplete && onLinkingComplete();
        },
        onSettled: () => {
            setSelectedRiskToUse(null);
        }
    });

    if (fetchingInprocess) {
        return (
            <div className="text-center py-4">
                <Spinner size="sm" />{" "}
                <span className="text-muted">
                    {t('Loading suggested risks')}…
                </span>
            </div>
        );
    }

    const categories = Object.entries(suggestionList || {}).filter(
        ([_, risks]) => risks?.length
    );

    if (!categories.length) {
        return (
            <div className="text-muted text-center py-4">
                {t('No suggested risks found. You can create a new risk for this third party.')}
            </div>
        );
    }

    let index = 0;

    return (
        <div>
            <Alert color="info">
                <div className="d-flex align-items-center justify-content-start gap-2">
                    <RiInformationLine size={32} />
                    <p className="mb-0">
                        {t('Choose one of the existing risks created by admins for other third parties. If none of these risks apply, you can create a new risk.')}
                    </p>
                </div>
            </Alert>

            <Accordion defaultActiveKey={0} alwaysOpen>
                {categories.filter(([key]) => CATEGORY_CONFIG[key]).map(([key, risks]) => {
                    const config = CATEGORY_CONFIG[key];

                    return (
                        <Accordion.Item
                            eventKey={index++}
                            className="border-none cusom-accordion-item shadow-none mb-3"
                        >
                            <Accordion.Header className="custom-accordion-header">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="custom-div-header">
                                        {t(config.title)}
                                    </span>
                                    <Badge className="bg-secondary" pill color="secondary">
                                        {risks.length}
                                    </Badge>
                                </div>
                            </Accordion.Header>

                            <Accordion.Body
                                style={{
                                    maxHeight: '340px',
                                    overflowY: 'auto'
                                }}
                            >
                                <div className="mb-3 text-muted d-flex align-items-start gap-2">
                                    <RiInformationLine size={18} />
                                    <p className="mb-0">{t(config.description)}</p>
                                </div>

                                <Table
                                    hover 
                                    size="sm"
                                >
                                    <thead
                                        style={{
                                            position: 'sticky',
                                            top: -16,
                                            background: 'white',
                                            zIndex: 2
                                        }}
                                    >
                                        <tr>
                                            <th>{t('Risk Rating')}</th>
                                            <th>{t('Risk ID')}</th>
                                            <th>{t('Title')}</th>
                                            <th>{t('Category')}</th>
                                            <th>{t('Sub Category')}</th>
                                            <th>{t('Source Third Party')}</th>
                                            <th className="text-end">
                                                {t('Action')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {risks.map(risk => {
                                            const isLinked = false;

                                            return (
                                                <tr key={risk.id}>
                                                    <td>
                                                        <div>
                                                            <span className="dt-risk-rating-bullet"
                                                                style={{
                                                                    backgroundColor: `#${risk.result_color}`
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {"R" + risk.secondary_id}
                                                    </td>
                                                    <td>
                                                        {risk.title}
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <span className="dt-list-col-result-description">
                                                                {t(risk.sub_category)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <span className="dt-list-col-result-description">
                                                                {t(risk.category)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {(risk.linked_suppliers || []).map((supplier) => {
                                                            return supplier.name
                                                        }).join(', ')}
                                                    </td>
                                                    <td className="text-end">
                                                        <Button
                                                            size="sm"
                                                            color="primary"
                                                            outline={!isLinked}
                                                            disabled={isLinked}
                                                            onClick={() => setSelectedRiskToUse(risk)}
                                                        >
                                                            <RiLinkM size={14} className="me-1" />
                                                            {isLinked ? "Linked" : "Use Risk"}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>

                                <small className="text-muted">
                                    {t('Reused risks are shared across third parties. Changes may affect all linked third parties.')}
                                </small>
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>

            <ConfirmUseRiskModal 
                t={t}
                isOpen={!!selectedRiskToUse}
                toggle={() => setSelectedRiskToUse(null)}
                onConfirm={(risk) => handleLinkRisk.mutate(risk)}
                selectedRisk={selectedRiskToUse}
                isLoading={handleLinkRisk.isLoading}
            />
        </div>
    );
}

export default withNamespaces()(
    memo(ExistingRisksContent)
);